import React from "react"
import PropTypes from "prop-types"
import cn from "classnames"
import popup, { PopupPanel } from "@infotech/uikit-popup-factory"
import { Box } from "@infotech/uikit-grid"
import { errorStyle, warningStyle } from "src/helpers/invalidStyle"
import TooltipWrapper from "src/components/Tooltips/TooltipWrapper"
import DeletedIcon from "src/icons/deleted.svg"
import styles from "./styles.css"
import { useHover } from "./hooks"
import ResourcePopupContent from "./ResourcePopup"

const ResourcePopup = popup(Box, {
  on: `click`,
  placement: `right-top`,
  realTimeRender: true,
})

const ResourceGroup = ({
  group,
  group: { rowIndex },
  plan,
  planAllResourcesValidate,
  planResourceTypes,
  resourceTypes,
  height,
  hoverIndex,
  setHoverIndex,
}) => {
  const [hoverRef] = useHover({
    hoverIndex,
    setHoverIndex,
    rowIndex,
  })

  const notValidResource =
    planAllResourcesValidate && planAllResourcesValidate[group.id]

  const groupWithHover =
    group &&
    !group.offPlan &&
    !group.isDeleted &&
    !notValidResource &&
    hoverIndex === rowIndex

  const groupErrorStyle =
    notValidResource && planAllResourcesValidate[group.id].error
      ? errorStyle
      : warningStyle

  const validateResourceAttr =
    notValidResource && planAllResourcesValidate[group.id].error
      ? `error`
      : `warning`

  const groupStyle = {
    height,
    ...(plan && planAllResourcesValidate[group.id] ? groupErrorStyle : null),
  }

  const Resource = (
    <Box
      loose
      pt="1md"
      pl="2md"
      pr="2md"
      flex
      className={cn(
        styles.GroupItem,
        !notValidResource && {
          [styles.GroupItem_offPlan]: group.offPlan,
          [styles.GroupItem_deleted]: group.isDeleted,
        },
        groupWithHover && styles.GroupHover,
      )}
      style={groupStyle}
      ref={hoverRef}
    >
      {group.isDeleted ? (
        <Box loose style={{ position: `absolute` }}>
          <DeletedIcon
            className={cn(styles.DeletedIcon, {
              [styles.DeletedIcon_black]: group.isDeleted && notValidResource,
            })}
          />
        </Box>
      ) : null}
      <Box loose w="100%" pl={group.isDeleted ? `4md` : 0}>
        <strong>{group.title}</strong>
        {group.label && (
          <TooltipWrapper
            className={styles.Tooltip}
            tooltipLabel={group.label}
            tooltipPosition="bottom"
            tooltipStyle={{
              margin: `-25px 0 0 -142px`,
              width: `200px`,
            }}
            tooltipFixed
          >
            <Box
              loose
              className={styles.ResourceLabel}
              style={{
                color:
                  group.offPlan && plan && !notValidResource ? `#888` : `#333`,
              }}
            >
              {group.label}
            </Box>
          </TooltipWrapper>
        )}
        <div className={styles.Hoverbar} />
      </Box>
    </Box>
  )

  return (
    <ResourcePopup className={styles.PopupOverlay}>
      {plan && planAllResourcesValidate[group.id] ? (
        <div data-testid={`calendar-resource-validate-${validateResourceAttr}`}>
          {Resource}
        </div>
      ) : (
        Resource
      )}
      <ResourcePopup.Content>
        {({ placement }) => (
          <PopupPanel placement={placement}>
            <ResourcePopupContent
              isPlan={plan}
              resourceData={group}
              resourceValidate={planAllResourcesValidate}
              resourceTypes={plan ? planResourceTypes.data : resourceTypes.data}
            />
          </PopupPanel>
        )}
      </ResourcePopup.Content>
    </ResourcePopup>
  )
}
ResourceGroup.propTypes = {
  plan: PropTypes.bool.isRequired,
  planAllResourcesValidate: PropTypes.arrayOf(
    PropTypes.shape({
      errors: PropTypes.bool,
      violations: PropTypes.shape({
        level: PropTypes.string.isRequired,
        message: PropTypes.string.isRequired,
      }),
    }),
  ),
  planResourceTypes: PropTypes.shape({
    data: PropTypes.array,
  }),
  resourceTypes: PropTypes.shape({
    data: PropTypes.array,
  }),
  group: PropTypes.shape({
    isLast: PropTypes.bool,
    rowIndex: PropTypes.number,
    title: PropTypes.string.isRequired,
    offPlan: PropTypes.bool,
    isDeleted: PropTypes.bool,
  }),
  height: PropTypes.number.isRequired,
}
ResourceGroup.defaultProps = {
  group: {},
  planAllResourcesValidate: [],
  planResourceTypes: {},
  resourceTypes: {},
}

export default ResourceGroup
